import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import {
  FOR_INVESTORS_SECTIONS,
  FOR_INVESTORS_SUB_SECTIONS,
} from 'constants/businessToolSections/forInvestors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { NumericBox } from 'components/BusinessToolForm/components/NumericBox';
import { KeySuppliers } from 'components/BusinessToolForm/Sections/BusinessOperations/PartnersSection/KeySuppliers';
import {
  keySuppliersCharacterizeValues,
  PARTNERS_NAMES,
  prefix as partnersPrefix,
} from 'components/BusinessToolForm/Sections/BusinessOperations/PartnersSection/utils/form';
import styles from 'components/BusinessToolForm/Sections/BusinessOperations/TechnologicalProcess/TechnologicalProcess.module.scss';
import {
  stagesValues,
  TECHNOLOGICAL_PROCESS_NAMES,
  prefix as techProcessPrefix,
} from 'components/BusinessToolForm/Sections/BusinessOperations/TechnologicalProcess/utils/form';
import {
  WORKING_RESOURCES_NAMES,
  prefix as workingResourcesPrefix,
} from 'components/BusinessToolForm/Sections/BusinessOperations/WorkingResources/utils/form';
import { STAGES_NAMES } from 'components/BusinessToolForm/Sections/ImplementationPlan/utils/form';

import { concatWithDot } from 'helpers/string';

export const TechProcess: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_INVESTORS_SECTIONS.BUSINESS_OPERATIONS,
    FOR_INVESTORS_SUB_SECTIONS.FOR_INVESTORS_BUSINESS_OPERATIONS.TECHNOLOGICAL_PROCESS
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('businessOperations.title')}
          badgeText={t('businessOperations.technologicalProcess.title')}
          section={FOR_INVESTORS_SECTIONS.BUSINESS_OPERATIONS}
        />
      </FormSectionCard>
      {/* 6.4.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.technologicalProcess.technology')}
        aiFieldName={concatWithDot(techProcessPrefix, TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(techProcessPrefix, TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY)}
          name={concatWithDot(techProcessPrefix, TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 6.4.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.technologicalProcess.stages.title')}
        collapsible
      >
        <FieldArrayComponent
          name={concatWithDot(techProcessPrefix, TECHNOLOGICAL_PROCESS_NAMES.STAGES)}
          initialItem={stagesValues}
          buttonText={t('businessOperations.technologicalProcess.stages.stage')}
          fieldArrayStyles={{ removeButton: styles.removeButton }}
          disabled={!canAccess}
        >
          {(namePrefix, index) => (
            <Grid spacing={1} container>
              <Grid item xs="auto">
                <NumericBox number={index + 1} variant="outlined" sx={{ marginTop: 2.5 }} />
              </Grid>
              <Grid item xs>
                <TextInputControl
                  control={control}
                  id={concatWithDot(namePrefix, STAGES_NAMES.STAGE)}
                  name={concatWithDot(namePrefix, STAGES_NAMES.STAGE)}
                  label={t('businessOperations.technologicalProcess.stages.stage')}
                  disabled={!canAccess}
                  maxLength={150}
                />
              </Grid>
            </Grid>
          )}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 6.5.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.workingResources.necessaryRecourses')}
        aiFieldName={concatWithDot(workingResourcesPrefix, WORKING_RESOURCES_NAMES.NECESSARY_RECOURSES)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(workingResourcesPrefix, WORKING_RESOURCES_NAMES.NECESSARY_RECOURSES)}
          name={concatWithDot(workingResourcesPrefix, WORKING_RESOURCES_NAMES.NECESSARY_RECOURSES)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 6.6.1 */}
      <FormSectionCard disabled={!canAccess} title={t('businessOperations.partners.keySuppliers.title')} collapsible>
        <FieldArrayComponent
          name={concatWithDot(partnersPrefix, PARTNERS_NAMES.KEY_SUPPLIERS)}
          initialItem={keySuppliersCharacterizeValues}
          buttonText={t('businessOperations.partners.keySuppliers.buttonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <KeySuppliers name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
    </>
  );
};
